import React from 'react';
import {gql} from '@apollo/client';
import {useMutation, useQuery} from '@apollo/client/react';
import {Watch} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {SxProps, Theme} from '@mui/material/styles';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import dayjs, {Dayjs} from 'dayjs';
import {LatLngLiteral} from 'leaflet';
import {FormContainer, useFormContext} from 'react-hook-form-mui';
import {toast} from 'react-toastify';

import {useDialogContext} from 'components/dialog/DialogContext';
import {Button} from 'components/forms/Buttons';
import ButtonDialog from 'components/forms/ButtonDialog';
import {DateTimePickerInput, SelectInput, TextInput} from 'components/forms/Inputs';
import {useListContext} from 'components/list/ListContext';
import SubEventMapField from 'components/SubEvent/SubEventMapField';

const FORM_ID = 'create_event_from_activity';
const HALF_HOUR_MS = 30 * 60 * 1000;
const USER_SEARCH_MIN_CHARS = 2;
const USER_SEARCH_LIMIT = 25;
const USER_SEARCH_DEBOUNCE_MS = 300;

const eventCategoryOptions = [
  {id: 'test', label: 'Test'},
  {id: 'orienteering', label: 'Orienteering'},
  {id: 'running', label: 'Running'}
];

const eventDateTimeViews = ['year', 'month', 'day', 'hours', 'minutes'] as const;

type ActivityProvider = 'GARMIN' | 'SUUNTO' | 'COROS';

type ActivityUser = {id: number; name?: string | null; firstName?: string | null; lastName?: string | null};

const userLabel = (user: ActivityUser) =>
  user.name?.trim() || `${user.firstName || ''} ${user.lastName || ''}`.trim() || `User ${user.id}`;

type ActivitySummary = {
  id: number;
  name?: string | null;
  provider: ActivityProvider;
  startTimeInSeconds: number;
  durationInSeconds?: number | null;
};

type ActivityDetail = ActivitySummary & {
  points: Array<{lat?: number | null; lng?: number | null}>;
};

type MapPin = {order: number; x: number; y: number};

type FormValues = {
  name: string;
  category: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  location?: {lat: number; lng: number};
  zoom?: number;
  provider?: ActivityProvider;
  activityId?: number;
  userId?: number;
  file?: File;
  mapName?: string;
  height?: number;
  width?: number;
  pins?: MapPin[];
  gpsTrack?: LatLngLiteral[];
  createControls?: boolean;
};

const defaultValues: FormValues = {
  name: '',
  category: 'orienteering',
  startDate: null,
  endDate: null,
  zoom: 14,
  pins: [],
  gpsTrack: [],
  createControls: true
};

const SEARCH_USERS = gql`
  query SearchUsersForEventActivity($filters: UserFilters, $size: Int) {
    items: allUsers(page: 1, size: $size, filters: $filters) {
      id
      name
      firstName
      lastName
    }
  }
`;

const MY_ACTIVITIES = gql`
  query MyActivities($userId: Int!, $from: Int, $to: Int) {
    items: myActivities(userId: $userId, from: $from, to: $to) {
      id
      name
      provider
      startTimeInSeconds
      durationInSeconds
    }
  }
`;

const MY_ACTIVITY = gql`
  query MyActivity($provider: ActivityProvider!, $id: Int!, $userId: Int!) {
    item: myActivity(provider: $provider, id: $id, userId: $userId) {
      id
      name
      provider
      startTimeInSeconds
      durationInSeconds
      points {
        lat
        lng
      }
    }
  }
`;

const CREATE_EVENT_FROM_ACTIVITY = gql`
  mutation CreateEventFromActivity($payload: CreateEventFromActivityPayload!) {
    item: createEventFromActivity(payload: $payload) {
      name
      slug
    }
  }
`;

const providerLabel = (provider: ActivityProvider): string => {
  switch (provider) {
    case 'GARMIN':
      return 'Garmin';
    case 'SUUNTO':
      return 'Suunto';
    case 'COROS':
      return 'COROS';
    default: {
      const _exhaustive: never = provider;
      return _exhaustive;
    }
  }
};

const isActivityProvider = (value: string): value is ActivityProvider =>
  value === 'GARMIN' || value === 'SUUNTO' || value === 'COROS';

const parseActivityKey = (key: string): {provider: ActivityProvider; id: number} | null => {
  const separator = key.indexOf(':');
  if (separator < 0) return null;
  const provider = key.slice(0, separator);
  const id = Number(key.slice(separator + 1));
  if (!isActivityProvider(provider) || !Number.isFinite(id)) return null;
  return {provider, id};
};

const roundDownToHalfHour = (ms: number) => {
  const date = new Date(ms);
  date.setSeconds(0, 0);
  const minutes = date.getMinutes();
  date.setMinutes(minutes < 30 ? 0 : 30);
  return date.getTime();
};

const roundUpToHalfHour = (ms: number) => {
  const date = new Date(ms);
  const seconds = date.getSeconds();
  const millis = date.getMilliseconds();
  date.setSeconds(0, 0);
  const minutes = date.getMinutes();
  if (seconds === 0 && millis === 0 && (minutes === 0 || minutes === 30)) {
    return date.getTime();
  }
  if (minutes < 30) {
    date.setMinutes(30);
    return date.getTime();
  }
  date.setHours(date.getHours() + 1, 0, 0, 0);
  return date.getTime();
};

/** Start is activity start minus 30 minutes, floored to :00 or :30. End is activity end plus 30 minutes, ceiled the same way. */
const eventWindowFromActivity = (startSec: number, durationSec?: number | null) => {
  const startMs = startSec * 1000;
  const endMs = startMs + Math.max(0, durationSec ?? 0) * 1000;
  return {
    start: dayjs(roundDownToHalfHour(startMs - HALF_HOUR_MS)),
    end: dayjs(roundUpToHalfHour(endMs + HALF_HOUR_MS))
  };
};

const trackCenter = (points: Array<{lat?: number | null; lng?: number | null}>) => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const point of points) {
    if (point.lat == null || point.lng == null) continue;
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) continue;
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLng = Math.min(minLng, point.lng);
    maxLng = Math.max(maxLng, point.lng);
  }
  if (!Number.isFinite(minLat)) return null;
  return {lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2};
};

const defaultActivityFromDate = () => dayjs().subtract(7, 'day').startOf('day');

const activityWhen = (activity: ActivitySummary) => {
  const started = dayjs.unix(activity.startTimeInSeconds).format('D MMM YYYY HH:mm');
  if (activity.durationInSeconds == null) return started;
  return `${started}–${dayjs.unix(activity.startTimeInSeconds + activity.durationInSeconds).format('HH:mm')}`;
};

type SubmitState = {
  submitting: boolean;
  setSubmitting: (value: boolean) => void;
};

const SubmitStateContext = React.createContext<SubmitState>({
  submitting: false,
  setSubmitting: () => {}
});

const SubmitCreateButton = () => {
  const {submitting} = React.useContext(SubmitStateContext);
  return (
    <Button type="submit" form={FORM_ID} disabled={submitting}>
      {submitting ? 'Creating…' : 'Create'}
    </Button>
  );
};

const ActivityEventFields = () => {
  const {isOpen} = useDialogContext();
  const {reset, setValue} = useFormContext<FormValues>();
  const [activityKey, setActivityKey] = React.useState('');
  const [selectedUser, setSelectedUser] = React.useState<ActivityUser | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [fromDate, setFromDate] = React.useState<Dayjs | null>(defaultActivityFromDate);
  const [toDate, setToDate] = React.useState<Dayjs | null>(null);
  const loadedKey = React.useRef('');

  const selected = parseActivityKey(activityKey);
  const fromSec = fromDate?.isValid() ? fromDate.startOf('day').unix() : undefined;
  const toSec = toDate?.isValid() ? toDate.endOf('day').unix() : undefined;
  const dateRangeInvalid = fromSec != null && toSec != null && fromSec > toSec;

  const clearActivitySelection = () => {
    setActivityKey('');
    loadedKey.current = '';
    setValue('provider', undefined);
    setValue('activityId', undefined);
    setValue('pins', []);
    setValue('gpsTrack', []);
  };

  React.useEffect(() => {
    const timer = window.setTimeout(() => setSearch(inputValue.trim()), USER_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [inputValue]);

  const {data: usersData, loading: usersLoading} = useQuery<{items: ActivityUser[]}>(SEARCH_USERS, {
    variables: {filters: {search}, size: USER_SEARCH_LIMIT},
    skip: !isOpen || search.length < USER_SEARCH_MIN_CHARS,
    fetchPolicy: 'network-only'
  });

  const {data: listData, loading: listLoading, error: listError} = useQuery<{items: ActivitySummary[]}>(MY_ACTIVITIES, {
    variables: {userId: selectedUser?.id, from: fromSec ?? null, to: toSec ?? null},
    skip: !isOpen || !selectedUser?.id || dateRangeInvalid,
    fetchPolicy: 'network-only'
  });

  const {data: activityData, loading: activityLoading, error: activityError} = useQuery<{item: ActivityDetail}>(MY_ACTIVITY, {
    variables: {provider: selected?.provider, id: selected?.id, userId: selectedUser?.id},
    skip: !isOpen || !selected || !selectedUser?.id,
    fetchPolicy: 'network-only'
  });

  React.useEffect(() => {
    if (!isOpen) {
      setActivityKey('');
      setSelectedUser(null);
      setInputValue('');
      setSearch('');
      setFromDate(defaultActivityFromDate());
      setToDate(null);
      loadedKey.current = '';
      reset(defaultValues);
    }
  }, [isOpen, reset]);

  const detail = selected && selectedUser ? activityData?.item : undefined;
  const track = React.useMemo(() => {
    const points = detail?.points ?? [];
    return points
      .filter((point): point is {lat: number; lng: number} => point.lat != null && point.lng != null && Number.isFinite(point.lat) && Number.isFinite(point.lng))
      .map((point) => ({lat: point.lat, lng: point.lng}));
  }, [detail]);

  React.useEffect(() => {
    if (!detail) return;
    const key = `${selectedUser?.id ?? ''}:${detail.provider}:${detail.id}`;
    if (loadedKey.current === key) return;
    loadedKey.current = key;

    const eventWindow = eventWindowFromActivity(detail.startTimeInSeconds, detail.durationInSeconds);
    const center = trackCenter(detail.points ?? []);
    const label = `${detail.name?.trim() || 'Activity'} ${dayjs.unix(detail.startTimeInSeconds).format('D MMM YYYY')}`;
    setValue('name', label);
    setValue('mapName', label);
    setValue('category', 'orienteering');
    setValue('startDate', eventWindow.start);
    setValue('endDate', eventWindow.end);
    setValue('provider', detail.provider);
    setValue('activityId', detail.id);
    if (selectedUser) {
      setValue('userId', selectedUser.id);
    }
    setValue('zoom', 14);
    setValue('pins', []);
    if (center) {
      setValue('location', center);
    }
    if (track.length < 2) {
      toast.error('This activity has no GPS points');
    }
  }, [detail, selectedUser, setValue, track.length]);

  const activities = selectedUser ? (listData?.items ?? []) : [];
  const users = React.useMemo(() => {
    const items = usersData?.items || [];
    if (selectedUser && !items.some((user) => user.id === selectedUser.id)) {
      return [selectedUser, ...items];
    }
    return items;
  }, [usersData?.items, selectedUser]);

  const chooseUser = (user: ActivityUser | null) => {
    setSelectedUser(user);
    setValue('userId', user?.id);
    clearActivitySelection();
  };

  const changeActivityDate = (which: 'from' | 'to', value: Dayjs | null) => {
    switch (which) {
      case 'from':
        setFromDate(value);
        break;
      case 'to':
        setToDate(value);
        break;
      default: {
        const _exhaustive: never = which;
        return _exhaustive;
      }
    }
    clearActivitySelection();
  };

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        Search for a user, then choose one of their watch activities. Start and end are set 30 minutes around it and
        snapped to the half hour. The event center is the middle of the GPS track.
      </Typography>
      <Autocomplete
        size="small"
        options={users}
        loading={usersLoading}
        value={selectedUser}
        inputValue={inputValue}
        filterOptions={(options) => options}
        getOptionLabel={userLabel}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => chooseUser(value)}
        onInputChange={(_, value) => setInputValue(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="User"
            helperText={
              search.length > 0 && search.length < USER_SEARCH_MIN_CHARS
                ? `Type at least ${USER_SEARCH_MIN_CHARS} characters`
                : 'Activities are loaded for the selected user'
            }
          />
        )}
      />
      {!selectedUser ? (
        <Typography variant="body2" color="text.secondary">
          Search and select a user to load their activities.
        </Typography>
      ) : (
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
          <DatePicker
            label="From"
            value={fromDate}
            onChange={(value) => changeActivityDate('from', value)}
            slotProps={{textField: {size: 'small', fullWidth: true}}}
          />
          <DatePicker
            label="To"
            value={toDate}
            onChange={(value) => changeActivityDate('to', value)}
            slotProps={{textField: {size: 'small', fullWidth: true}}}
          />
        </Stack>
      )}
      {!selectedUser ? null : dateRangeInvalid ? (
        <Typography color="error" variant="body2">
          From date must be on or before the to date.
        </Typography>
      ) : listLoading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
          <CircularProgress size={24} />
        </Box>
      ) : listError ? (
        <Typography color="error" variant="body2">
          {listError.message}
        </Typography>
      ) : activities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {fromDate || toDate
            ? 'No activities with GPS in this date range.'
            : 'No Garmin, Suunto, or COROS activities with GPS were found for this user.'}
        </Typography>
      ) : (
        <RadioGroup value={activityKey} onChange={(event) => setActivityKey(event.target.value)}>
          {activities.map((activity) => (
            <FormControlLabel
              key={`${activity.provider}:${activity.id}`}
              value={`${activity.provider}:${activity.id}`}
              control={<Radio size="small" />}
              label={`${providerLabel(activity.provider)}: ${activity.name || 'Activity'} (${activityWhen(activity)})`}
            />
          ))}
        </RadioGroup>
      )}
      {activityLoading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
          <CircularProgress size={22} />
        </Box>
      ) : null}
      {activityError ? (
        <Typography color="error" variant="body2">
          {activityError.message}
        </Typography>
      ) : null}
      {detail && track.length >= 2 ? (
        <React.Fragment>
          <TextInput required fullWidth label="Name" name="name" />
          <SelectInput fullWidth required name="category" label="Category" options={eventCategoryOptions} />
          <DateTimePickerInput
            fullWidth
            required
            format="YYYY-MM-DD HH:mm"
            views={eventDateTimeViews}
            name="startDate"
            label="Start"
          />
          <DateTimePickerInput
            fullWidth
            required
            format="YYYY-MM-DD HH:mm"
            views={eventDateTimeViews}
            name="endDate"
            label="End"
          />
          <SubEventMapField
            presetTrack={track}
            maxPins={2}
            event={{location: trackCenter(detail.points ?? []) || undefined, zoom: 14}}
          />
        </React.Fragment>
      ) : null}
    </Stack>
  );
};

const CreateEventFromActivityForm = () => {
  const {close} = useDialogContext();
  const {refetch} = useListContext();
  const {setSubmitting} = React.useContext(SubmitStateContext);
  const [createEventFromActivity] = useMutation<{item?: {name?: string; slug?: string}}>(CREATE_EVENT_FROM_ACTIVITY, {
    context: {
      headers: {
        'apollo-require-preflight': true
      }
    }
  });

  const handleSubmit = async (values: FormValues) => {
    if (!values.userId) {
      toast.error('Select a user');
      return;
    }
    if (!values.provider || values.activityId == null) {
      toast.error('Select an activity');
      return;
    }
    if (!values.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!values.startDate || !values.endDate || !values.endDate.isAfter(values.startDate)) {
      toast.error('End time must be after start time');
      return;
    }
    if (!values.location) {
      toast.error('Activity has no GPS center');
      return;
    }
    const includeMap = Boolean(values.file);
    if (includeMap && (!values.pins || values.pins.length < 2)) {
      toast.error('Place the start and end pins on the map, or remove the image');
      return;
    }
    if (includeMap && (!values.gpsTrack || values.gpsTrack.length < 2)) {
      toast.error('Activity GPS track is too short to georeference the map');
      return;
    }

    setSubmitting(true);
    try {
      const result = await createEventFromActivity({
        variables: {
          payload: {
            name: values.name.trim(),
            category: values.category,
            startDate: values.startDate.toDate().toISOString(),
            endDate: values.endDate.toDate().toISOString(),
            location: values.location,
            zoom: values.zoom ?? 14,
            provider: values.provider,
            activityId: values.activityId,
            userId: values.userId,
            ...(includeMap
              ? {
                  file: values.file,
                  mapName: values.mapName?.trim() || values.name.trim(),
                  height: values.height,
                  width: values.width,
                  pins: (values.pins ?? []).map((pin, index) => ({
                    order: pin.order ?? index,
                    x: pin.x,
                    y: pin.y
                  })),
                  gpsTrack: (values.gpsTrack ?? []).map((point) => ({lat: point.lat, lng: point.lng})),
                  createControls: values.createControls !== false
                }
              : {})
          }
        }
      });
      const created = result.data?.item;
      if (!created) {
        toast.error(result.error?.message || 'Could not create the event');
        return;
      }
      toast.success(`Event ${created.name || ''} created`);
      refetch?.();
      close();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create the event');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormContainer<FormValues>
      FormProps={{id: FORM_ID, noValidate: true}}
      defaultValues={defaultValues}
      onSuccess={handleSubmit}
    >
      <ActivityEventFields />
    </FormContainer>
  );
};

const CreateEventFromActivityButton = ({sx}: {sx?: SxProps<Theme>}) => {
  const [submitting, setSubmitting] = React.useState(false);

  return (
    <SubmitStateContext.Provider value={{submitting, setSubmitting}}>
      <ButtonDialog
        buttonLabel="From activity"
        startIcon={<Watch />}
        dialogTitle="Create event from activity"
        maxWidth="lg"
        sx={sx}
        primaryButton={<SubmitCreateButton />}
      >
        <CreateEventFromActivityForm />
      </ButtonDialog>
    </SubmitStateContext.Provider>
  );
};

export default CreateEventFromActivityButton;
